import { Component, Vue, Prop, Watch, Inject, Emit } from 'vue-property-decorator';
import { CreditCardTokenData, createCreditCardTokenData, PaymentProcessorOptions, createPaymentProcessorOptions, SavedCreditCard, ApiErrorResponse, BasicSelectListItem, createBasicSelectListItem, AutoPayOption, PaymentProcessorEnum, PaymentPlan } from '@/common/types/AllTypesExports';
import { PropOptions } from 'vue';
import StripeCardInput from './StripeCardInput.vue';
import QuickBooksPaymentsCardInput from './QuickBooksPaymentsCardInput.vue';
import CreditCardEntryForm from './CreditCardEntryForm.vue';
import WarningAlert from '../../components/errors/WarningAlert.vue';
import { default as CreditCardEntryFormClass } from './CreditCardEntryFormVue';
import FieldValidationErrors from '../../components/errors/FieldValidationErrors.vue';
import { paymentsRepo } from '@/common/repositories/PaymentsRepo';
import { AxiosResponse } from 'axios';
import CustomerSelector from '../membership/CustomerSelector.vue';
import { INoviNsPaymentGlobals } from '../../common/types/Payment/NoviNsPaymentGlobals';
import { DtoCurrentUser } from '../../common/types/user/DtoCurrentUser';
import StripePaymentForm from './StripePaymentForm.vue';
import StripePaymentFormClass from './StripePaymentFormVue';

declare let noviNs: INoviNsPaymentGlobals;

@Component({
    name: 'CreditCardContainer',
    components: {
        StripeCardInput,
        QuickBooksPaymentsCardInput,
        FieldValidationErrors,
        CreditCardEntryForm,
        WarningAlert,
        CustomerSelector,
        StripePaymentForm
    }
})
export default class CreditCardContainer extends Vue {
    @Prop({ required: true, default: () => createPaymentProcessorOptions() } as PropOptions<PaymentProcessorOptions>)
    processorOptions!: PaymentProcessorOptions;

    @Prop({ required: true, default: () => "" })
    donateTransactionFeeLabel!: string;

    @Prop({ required: false, default: () => "" })
    donateTransactionFeeDescription!: string;

    @Prop({ required: false, default: () => 0 })
    donateTransactionFeePercentage!: number;

    @Prop({ required: false, default: () => "" })
    donateTransactionFeeChargeType!: string;

    @Prop({ required: false, default: () => true })
    creditCardActive!: boolean;

    @Prop({ required: false, default: () => true })
    paymentPlansActive!: boolean;

    @Prop({ required: false, default: () => "" })
    cardRestrictionMessage!: string;

    @Prop({ required: false, default: () => false })
    locked!: boolean;

    @Prop({ required: false, default: () => false })
    forceSaveCard!: boolean;

    @Prop({ required: false, default: () => false })
    isDonatingTransactionFee!: boolean;

    @Prop({ required: false, default: () => 0 })
    readonly proposedDonateTransactionFeeAmount!: number;

    @Prop({ required: false, default: () => 0 })
    readonly eligibleForPaymentPlanAmount!: number;

    @Prop({ required: false, default: () => [] })
    readonly eligibleItemsForPaymentPlan!: [];

    @Prop({ required: false, default: () => 0 })
    readonly total!: number;

    @Prop({ required: false, default: () => [] })
    autoPayOptions!: AutoPayOption[];

    @Prop({ required: false, default: false })
    setMemberTypeDefaultBillableParty: boolean | undefined;

    @Inject('user')
    currentUser!: DtoCurrentUser;

    @Prop({ required: false, default: () => false })
    acceptPaymentPlanTerms!: boolean;

    savedCardId: string = "";
    creditCardPaymentProcessorModule: PaymentProcessorEnum | null = null;
    newCardSelected: boolean = false;
    failedToPullSavedCards: boolean = false;
    billableParties: BasicSelectListItem[] = [];
    billableParty = createBasicSelectListItem();
    errorMessage: string = '';
    localWarningMessage: string = '';
    isCheckingForBillableParties: boolean = false;
    isCheckingForPaymentPlans: boolean = false;
    noBillablePartiesMessage: string = 'No eligible billable parties found for credit card. If you believe this to be an error, please contact us.';
    isDonatingTransactionFeeMandatory: boolean = false;
    paymentPlans: PaymentPlan[] = [];
    currentPlanId: number = 0;

    creditCardTokenData = createCreditCardTokenData(<CreditCardTokenData>{
        storedCardId: this.savedCardId,
        last4Digits: this.savedCardId.substr(-4),
        billablePartyId: this.processorOptions.billablePartyId,
        autoPayOptions: this.autoPayOptions
    });

    @Inject('validator')
    $validator: any;

    get preventCreditCardUsageMessage() {
        return this.cardRestrictionMessage || this.localWarningMessage || '';
    }

    get preventCreditCardUsage() {
        return !!this.preventCreditCardUsageMessage.length;
    }

    get showSavedCards() {
        if (this.processorOptions && this.processorOptions.savedCards) {
            return this.processorOptions.enableSaveCreditCard && this.processorOptions.savedCards.length > 0;
        }
        return false;
    }

    get showDonateTransactionFeeCheckbox() {
        return this.proposedDonateTransactionFeeAmount > 0 && this.processorOptions.convenienceFeeIsEnabled;
    }

    get isDonatingTransactionFeeDefault() {
        return this.processorOptions.donateTransactionFeeChargeType?.toLowerCase() === 'OptOut'.toLowerCase() || this.processorOptions.donateTransactionFeeChargeType?.toLowerCase() === 'Mandatory'.toLowerCase();
    }

    get isForcedSaveCard() {
        return this.forceSaveCard || this.autoPayOptions.filter(x => x.enabled && !x.externalPaymentMethodId).length > 0;
    }

    get autoPayOptionsOrdered() {
        return this.autoPayOptions.sort((a, b) => Number(b.isCurrentUser) - Number(a.isCurrentUser))
    }

    get showAutoPay() {
        return this.processorOptions.enableAutoPay && this.autoPayOptions.length > 0 && this.creditCardPaymentProcessorModule == noviNs.paymentProcessor;
    }

    get paymentPlansCheckoutNotice() {
        return noviNs.paymentPlansCheckoutNotice;
    }

    get useStripeEmbed() {
        return noviNs.paymentProcessor == PaymentProcessorEnum.noviPay;
    }

    truncateName(name: string) {
        return (name.length > 30) ? name.substr(0, 29) + '...' : name;
    }

    expirationText(month: number, year: number) {
        return 'Exp. ' + (month < 10 ? '0' + month : month) + ' / ' + year;
    }

    @Emit('card-token-updated')
    emitCreditCardTokenUpdate() {
        return this.creditCardTokenData;
    }

    @Watch('isDonatingTransactionFee', { immediate: false })
    onIsDonatingTransactionFeeChange(value: boolean) {
        this.$emit('update:is-donating-transaction-fee', value);
    }

    @Watch('billableParty')
    onBillablePartyChange(newBillableParty: BasicSelectListItem) {
        Object.assign(this.billableParty, newBillableParty);
        this.creditCardTokenData.billablePartyId = this.billableParty.id;
        this.emitCreditCardTokenUpdate();
    }

    @Watch('isDonatingTransactionFee')
    onisDonatingTransactionFeeUpdated(isDonatingTransactionFee: boolean) {
        this.loadPaymentPlans(this.eligibleItemsForPaymentPlan);
    }

    @Watch('total')
    onTotalUpdated(total: number) {
        this.loadPaymentPlans(this.eligibleItemsForPaymentPlan);
    }

    @Watch('eligibleItemsForPaymentPlan', { immediate: true })
    onEligibleItemsForPaymentPlanChange(eligibleItems: []) {
        this.loadPaymentPlans(eligibleItems);
    }

    @Watch('paymentPlans')
    onPaymentPlansChange(value: PaymentPlan[]) {
        this.$emit('update:payment-plans', value);
    }

    @Watch('paymentPlansActive')
    @Emit('payment-plans-active-updated')
    onPaymentPlanActiveChange(value: boolean) {
        this.loadPaymentPlans(this.eligibleItemsForPaymentPlan);
    }

    loadPaymentPlans(eligibleItems: []) {
        this.isCheckingForPaymentPlans = true;

        if (this.currentUser == null) {
            return;
        }

        if (eligibleItems.length > 0 && this.eligibleForPaymentPlanAmount > 0) {
            paymentsRepo.getPaymentPlans(eligibleItems, this.total, this.eligibleForPaymentPlanAmount, this.currentUser.customerID ?? undefined)
                .then((paymentPlans: PaymentPlan[]) => {
                    this.paymentPlans = paymentPlans;
                })
                .catch((error: any) => {
                    // checking if manual retry exists
                    if (error.manualRetry) {
                        this.errorMessage = error.message;
                    } else {
                        this.errorMessage = error.data?.message || "Error loading payment plans";
                    }
                })
                .finally(() => {
                    this.isCheckingForPaymentPlans = false;
                });
        }
        else {
            this.paymentPlans = [];
        }

        if (this.paymentPlans.length > 0 && this.currentPlanId == 0) {
            this.currentPlanId = this.paymentPlans[0].planId;
        }
        else if (this.paymentPlans.length == 0) {
            this.currentPlanId = 0;
            this.paymentPlansActive = false;
        }
    }

    // Watch for external changes to billablePartyId (change purchaser)
    @Watch('processorOptions.billablePartyId')
    onBillablePartyIdChange(newBillablePartyId: number) {
        if (isNaN(newBillablePartyId)) {
            this.updateBillToDropdownSelector(null);
        }
        else if (newBillablePartyId) {
            this.isCheckingForBillableParties = true;
            paymentsRepo.getBillableParties('SalesReceipt', newBillablePartyId, this.setMemberTypeDefaultBillableParty)
                .then((billableCustomers: BasicSelectListItem[]) => {
                    this.billableParties.splice(0, this.billableParties.length, ...billableCustomers);
                    this.errorMessage = '';
                    this.localWarningMessage = '';

                    if (this.billableParties.length) {
                        const newBillableParty = this.billableParties.find(b => b.id === newBillablePartyId) ?? this.billableParties[0];
                        Object.assign(this.billableParty, newBillableParty);
                        this.onBillablePartyChange(this.billableParty);
                        this.updateBillToDropdownSelector(this.billableParty);
                    }
                    else {
                        this.localWarningMessage = this.noBillablePartiesMessage;
                    }

                    this.onIsCreditCardProcessingPrevented(this.preventCreditCardUsage);
                })
                .catch((error: any) => {
                    // checking if manual retry exists
                    if (error.manualRetry) {
                        this.errorMessage = error.message;
                    } else {
                        this.errorMessage = error.data?.message || "Error loading billable parties";
                    }
                })
                .finally(() => {
                    this.isCheckingForBillableParties = false;
                });
        }
    }

    onIsCreditCardProcessingPrevented(value: boolean) {
        this.$emit('on-is-credit-card-processing-prevented', value);
    }

    onCreditCardPaymentTokenReady(card: CreditCardTokenData) {
        this.$emit('on-payment-token-ready', card);
    }

    onErrorToggle(hasError: boolean, errorMsg: string) {
        this.$emit('on-error-toggle', hasError, errorMsg);
    }

    onBillingStateChange(billingState: string) {
        this.$emit('update:credit-card-billing-state', billingState);
    }

    expandNewCardEntry() {
        if (!this.locked) {
            this.savedCardId = "";
            this.newCardSelected = true;
            this.creditCardPaymentProcessorModule = noviNs.paymentProcessor;
        }
    }

    selectStoredCard(card: SavedCreditCard) {
        if (!this.locked) {
            this.newCardSelected = false;
            this.savedCardId = card.cardId;
            this.creditCardPaymentProcessorModule = card.paymentProcessorModule;

            if (this.savedCardId) {
                this.$emit('update:credit-card-billing-state', this.getSavedCardBillingState());
            }
        }
    }

    selectPaymentPlan(paymentPlan: PaymentPlan) {
        this.currentPlanId = paymentPlan.planId;
    }

    createPaymentToken() {

        if (this.savedCardId) {
            var savedCardToken = createCreditCardTokenData(<CreditCardTokenData>{
                storedCardId: this.savedCardId,
                autoPayOptions: this.autoPayOptions,
                last4Digits: this.savedCardId.substr(-4),
                billablePartyId: this.billableParty.id,
                billingState: this.getSavedCardBillingState(),
                paymentProcessorModule: this.creditCardPaymentProcessorModule,
                transactionNotes: this.creditCardTokenData.transactionNotes,
                selectedPaymentPlanId: this.paymentPlansActive ? this.currentPlanId : 0
            });
            this.onCreditCardPaymentTokenReady(savedCardToken);
        }
        else if (this.useStripeEmbed) {
            const stripeEmbed = <StripePaymentFormClass>this.$refs.cardStripeEmbed;
            stripeEmbed.createStripePaymentToken(this.autoPayOptions, this.billableParty.id, this.paymentPlansActive ? this.currentPlanId : 0);
        }
        else {
            let creditCardContainer = <CreditCardEntryFormClass>this.$refs.creditCardEntryForm;
            creditCardContainer.createPaymentToken(this.autoPayOptions, this.billableParty.id, this.paymentPlansActive ? this.currentPlanId : 0);
        }
    }

    created() {
        this.isDonatingTransactionFeeMandatory = this.processorOptions.donateTransactionFeeChargeType?.toLowerCase() === 'Mandatory'.toLowerCase();
    }

    mounted() {
        this.isDonatingTransactionFee = this.isDonatingTransactionFeeDefault;

        if (this.creditCardTokenData.billablePartyId) {
            this.isCheckingForBillableParties = true;
            paymentsRepo.getBillableParties('SalesReceipt', this.processorOptions.customerForBillableParties ?? this.creditCardTokenData.billablePartyId, this.setMemberTypeDefaultBillableParty)
                .then((billableCustomers: BasicSelectListItem[]) => {
                    this.billableParties.splice(0, this.billableParties.length, ...billableCustomers);
                    this.errorMessage = '';
                    this.localWarningMessage = '';

                    if (this.billableParties.length) {
                        const currentBillableParty = this.billableParties.find(b => b.isDefault) ?? this.billableParties[0];
                        Object.assign(this.billableParty, currentBillableParty);
                        this.onBillablePartyChange(this.billableParty);
                        this.updateBillToDropdownSelector(this.billableParty);
                    }
                    else {
                        this.localWarningMessage = this.noBillablePartiesMessage;
                    }

                    this.onIsCreditCardProcessingPrevented(this.preventCreditCardUsage);
                })
                .catch((error: any) => {
                    if (error.manualRetry) {
                        this.errorMessage = error.message;
                    } else {
                        this.errorMessage = error.data?.message || "Error loading billable parties";
                    }
                })
                .finally(() => {
                    this.isCheckingForBillableParties = false;
                });
        }

        if (this.processorOptions.unauthorized) {
            this.creditCardPaymentProcessorModule = noviNs.paymentProcessor;
            return;
        }

        if (this.processorOptions.enableSaveCreditCard && this.processorOptions.savedCards && this.processorOptions.savedCards.length === 0) {
            paymentsRepo.getSavedCreditCards()
                .then((savedCards: SavedCreditCard[]) => {
                    if (savedCards.length > 0) {
                        this.processorOptions.savedCards.splice(0, this.processorOptions.savedCards.length, ...savedCards);
                        this.savedCardId = this.processorOptions.savedCards[0].cardId;
                        this.creditCardPaymentProcessorModule = this.processorOptions.savedCards[0].paymentProcessorModule;
                    } else {
                        this.creditCardPaymentProcessorModule = noviNs.paymentProcessor;
                    }
                })
                .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => { });
        }
        else if (this.processorOptions.enableSaveCreditCard && this.processorOptions.savedCards && this.processorOptions.savedCards.length > 0) {
            this.savedCardId = this.processorOptions.savedCards[0].cardId;
            this.creditCardPaymentProcessorModule = this.processorOptions.savedCards[0].paymentProcessorModule;
        }

        if (this.processorOptions.enablePaymentPlans) {
            this.loadPaymentPlans(this.eligibleItemsForPaymentPlan);
        }
    }

    // Update the 'Bill To' drop down value if changed from outside the vue app
    updateBillToDropdownSelector(value: any) {
        const customerSelector = this.$refs.billablePartySelector as any;
        if (customerSelector && typeof customerSelector.setSelectedCustomer === 'function') {
            customerSelector.setSelectedCustomer(value);
        }
    }

    onGuestDataUpdated(name: string, guestName: boolean) {
        const index = guestName ? this.billableParties.findIndex(item => item.id == -1) : this.billableParties.findIndex(item => item.id == -2);

        if (index === -1) {
            if (guestName) {
                this.billableParties.push({
                    id: -1,
                    displayName: name,
                    taxable: true,
                    isDefault: false,
                });
            }
            else {
                this.billableParties.push({
                    id: -2,
                    displayName: name,
                    taxable: true,
                    isDefault: false,
                });
            }
        } else {
            this.billableParties[index].displayName = name;
        }
        if (this.billableParties.length === 1) {
            const newBillableParty = this.billableParties[0];
            Object.assign(this.billableParty, newBillableParty);
            this.onBillablePartyChange(this.billableParty);
            this.updateBillToDropdownSelector(this.billableParty);
        }
    }

    getSavedCardBillingState() {
        if (this.savedCardId) {
            var card = this.processorOptions.savedCards.find(x => x.cardId === this.savedCardId);

            return card?.billingState;
        }

        return "";
    }

    retryBillableParties() {
        this.errorMessage = "";
        let bpId = this.processorOptions.customerForBillableParties ?? this.creditCardTokenData.billablePartyId;
        this.isCheckingForBillableParties = true;
        paymentsRepo.getBillableParties('SalesReceipt', bpId, this.setMemberTypeDefaultBillableParty)
            .then((billableCustomers: BasicSelectListItem[]) => {
                this.billableParties.splice(0, this.billableParties.length, ...billableCustomers);
                this.errorMessage = '';
                this.localWarningMessage = '';

                if (this.billableParties.length) {
                    const selected = this.billableParties.find(b => b.id === bpId) ?? this.billableParties[0];
                    Object.assign(this.billableParty, selected);
                    this.onBillablePartyChange(this.billableParty);
                    this.updateBillToDropdownSelector(this.billableParty);
                } else {
                    this.localWarningMessage = this.noBillablePartiesMessage;
                }
            })
            .catch((error: any) => {
                this.errorMessage = error.data?.message || "Error loading billable parties";
                this.fallbackToPurchaser();
            })
            .finally(() => {
                this.isCheckingForBillableParties = false;
            });
    }

    fallbackToPurchaser() {
        const purchaserId = this.currentUser.customerID || 0;
        const purchaserName = this.currentUser.firstAndLastName || "Purchaser";
        const taxable = this.currentUser.taxable;

        if (purchaserId && purchaserId !== 0) {
            const fallbackParty: BasicSelectListItem = {
                id: purchaserId,
                displayName: purchaserName,
                taxable: taxable,
                isDefault: true
            };

            Object.assign(this.billableParty, fallbackParty);
            this.creditCardTokenData.billablePartyId = purchaserId;
            this.creditCardTokenData.transactionNotes = "Billable Party Fallback";
            this.onBillablePartyChange(this.billableParty);
            this.updateBillToDropdownSelector(this.billableParty);
            this.billableParties.push(fallbackParty);
            this.errorMessage = "";
        } else {
            this.errorMessage = this.errorMessage || "Error loading billable parties.";
        }
    }
}